/* eslint-disable no-param-reassign */
import {
  useRef,
  useCallback,
  ChangeEventHandler,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  CSSProperties,
} from "react"

import createStyle, { CustomTagArgs } from "@josephmark/createstyle"

const InlineInputEl = createStyle(
  "input",
  "hover:bg-empress/20 focus:bg-empress/20 -m-1 min-w-[3ch] resize-none appearance-none overflow-hidden rounded-sm border-none bg-transparent p-1"
)

export const InlineInput = forwardRef<
  HTMLInputElement,
  // eslint-disable-next-line react/require-default-props
  CustomTagArgs<HTMLInputElement> & { multiline?: boolean; noGrow?: boolean }
>(({ multiline = false, noGrow = false, style = {}, ...props }, fRef) => {
  const ref = useRef<HTMLInputElement>(null)
  const [useCSSSizing] = useState(() => CSS.supports("field-sizing", "content"))

  useImperativeHandle(fRef, () => ref.current!, [ref])

  const updateSize = useCallback(
    (target: HTMLInputElement) => {
      if (noGrow || useCSSSizing) {
        return
      }
      if (multiline) {
        target.style.height = "0px"
        target.style.height = `${target.scrollHeight}px`
      } else {
        target.style.width = "0px"
        target.style.width = `${target.scrollWidth}px`
      }
    },
    [multiline, noGrow, useCSSSizing]
  )

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      props?.onChange?.(e)
      updateSize(e.currentTarget)
    },
    [props, updateSize]
  )
  useEffect(() => {
    updateSize(ref.current!)
  }, [props.value, updateSize])

  useLayoutEffect(() => {
    const resizeFn = () => {
      updateSize(ref.current!)
    }
    window.addEventListener("resize", resizeFn)
    return () => {
      window.removeEventListener("resize", resizeFn)
    }
  }, [updateSize])
  return (
    <InlineInputEl
      ref={ref}
      as={multiline ? "textarea" : "input"}
      style={!noGrow && useCSSSizing ? ({ fieldSizing: "content", ...style } as CSSProperties) : style}
      {...props}
      onChange={onChange}
    />
  )
})

export default InlineInput
